import * as React from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { AppStorePoliciesDocument } from 'generated/resources/graphql'

import { useEffect, useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'
import type { Navigation } from 'modules/core/lib/navigation/context'
import type { NavigationPaths } from 'modules/core/lib/navigation/context'
import { useAsyncCan } from 'modules/core/lib/useAsyncCan'

import useFetchData from 'modules/app_store/hooks/useFetchData'
import configurator from 'modules/app_store/lib/SubscriptionAddonConfigurator'
import { AtsSettingsAppStoreRootPage } from 'modules/ats/pages/settings/AppStoreRootPage'
import { APPS } from 'modules/billing/lib/types'
import type SubscriptionAddon from 'modules/billing/store/models/SubscriptionAddon'
import ComplaintsSettings from 'modules/complaints/pages/Settings/RootPage'
import EmployeeOnboardingSettings from 'modules/employees/pages/Settings/RootPage'
import ExpensesV2Settings from 'modules/expenses/pages/settings'
import FinanceSettingsPage from 'modules/finance/pages/settings/FinanceSettingsPage'
import Page from 'modules/layout/components/Page'
import Header from 'modules/layout/components/Page/Header'
import PayrollBundleSettingsPage from 'modules/payroll_bundle/pages/settings/SettingsPage'

import { getActionWrapper } from './ActionWrapper'
import { RenderProps } from './ActionWrapper/TimeTrackingButton'
import OldCompetenciesSettings from './Apps/Competencies/Settings'
// Settings
import ExpensesSettings from './Apps/Expenses/Settings'
import GrowthPlansSettings from './Apps/GrowthPlans/Settings'
import LinkedinLimitedListingsSettings from './Apps/LinkedinLimitedListings/Settings'
import TimeTrackingSettings from './Apps/TimeTracking/Settings'
import TrainingsSettings from './Apps/Trainings/Settings'
import buildTabs from './tabs'

interface ContentProps {
  subscriptionAddon: SubscriptionAddon | null | undefined
}

const computeBack = (
  subscriptionAddon: SubscriptionAddon | null | undefined,
  navigation: Navigation<NavigationPaths>,
  canSeeAppStore: boolean
): string => {
  if (canSeeAppStore) return navigation.appStore.fullPath()

  if (!subscriptionAddon) return navigation.dashboard.fullPath()

  switch (subscriptionAddon.attributes.id) {
    case APPS.performance_v2:
      return navigation.performance.reviews.fullPath()

    case APPS.surveys:
      return navigation.engagement.surveys.fullPath()

    case APPS.shift_management:
      return navigation.shifts.fullPath()

    case APPS.competencies:
      return navigation.performance.competencies.fullPath()

    default:
      return navigation.dashboard.fullPath()
  }
}

const ContentSettings = ({ subscriptionAddon }: ContentProps) => {
  if (!subscriptionAddon) return null

  const AddonSettings:
    | ((props: { subscriptionAddon: SubscriptionAddon }) => React.ReactElement)
    | (() => React.ReactElement | null)
    | undefined = {
    [APPS.time_tracking]: TimeTrackingSettings,
    [APPS.ats]: AtsSettingsAppStoreRootPage,
    [APPS.expenses]: ExpensesSettings,
    [APPS.expenses_v2]: ExpensesV2Settings,
    [APPS.payroll_bundle]: PayrollBundleSettingsPage,
    [APPS.trainings]: TrainingsSettings,
    [APPS.competencies_custom_table]: OldCompetenciesSettings,
    [APPS.complaints]: ComplaintsSettings,
    [APPS.growth_plans]: GrowthPlansSettings,
    [APPS.employee_onboarding]: EmployeeOnboardingSettings,
    [APPS.linkedin_limited_listings]: LinkedinLimitedListingsSettings,
    [APPS.finance]: FinanceSettingsPage,
  }[subscriptionAddon.attributes.id]

  if (!AddonSettings) return null

  return <AddonSettings subscriptionAddon={subscriptionAddon} />
}

const Settings = () => {
  const { id } = useParams()
  const { loading, isNotFound, subscriptionAddon } = useFetchData(id)
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const { data: policiesData } = useQuery(AppStorePoliciesDocument)

  const canSeeAppStore = policiesData?.integrationsBase._policies.seeAppStore.can ?? false

  const { loading: loadingPermission, value: canManagePayroll } =
    useAsyncCan('payroll.manage_payroll')

  useUnsafeEffect(() => {
    let config = null

    if (subscriptionAddon) {
      config = configurator.getSubscriptionAddonConfig(subscriptionAddon, navigation)
    }

    if (
      (config && !config.hasSettings) ||
      (!loading && !subscriptionAddon?.attributes.enabled) ||
      (!loadingPermission && id === 'payroll_bundle' && !canManagePayroll)
    ) {
      navigate(navigation.appStore.id.general.fullPath({ id: String(id) }))
    }
  }, [loading, loadingPermission, id, canManagePayroll, navigate, navigation, subscriptionAddon])

  useEffect(() => {
    track(trackingEvents.appStore.goToAnyAppSettings, {
      app_name: subscriptionAddon?.id,
      app_page_path: location.pathname,
      app_page_url: window.location.href,
    })
  }, [subscriptionAddon?.id])

  const ActionWrapper = getActionWrapper(subscriptionAddon?.id)

  return (
    <Page
      isLoading={loading}
      notFound={{
        active: isNotFound,
        title: i18n.t('errors.page_not_found.title'),
        description: i18n.t('errors.page_not_found.description'),
      }}
    >
      <Page.Header>
        <ActionWrapper subscriptionAddon={subscriptionAddon}>
          {(actionProps: RenderProps) => (
            <Header.Tabs
              backTo={computeBack(subscriptionAddon, navigation, canSeeAppStore)}
              tabs={
                subscriptionAddon ? buildTabs(subscriptionAddon, navigation, canSeeAppStore) : []
              }
              action={
                actionProps
                  ? {
                      onClick: actionProps.onClick,
                      label: actionProps.label || '',
                      icon: 'add',
                    }
                  : undefined
              }
            />
          )}
        </ActionWrapper>
      </Page.Header>
      <Page.Body>
        <ContentSettings subscriptionAddon={subscriptionAddon} />
      </Page.Body>
    </Page>
  )
}

export default Settings
